import { v4 as uuidv4 } from "uuid";

import { Slide, Specimen } from "pwapi";

import { CptInput } from "custom-models";
import { CptCode } from "custom-models";
import { CaseDiagnosis } from "custom-models";

import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SpecimenDetail } from "shared/reducers/specimenDetailReducer";

import {
  getMostRecentScreenRequest,
  isScreened,
  isSpecimenScreened,
} from "./utils";

export const cptString = (code: CptCode) => {
  return `${code.code}${code.modifier ? `-${code.modifier}` : ""}`;
};

// Helper method for deconstructing CPT arrays sent by the API
export const reduceCptCodes = (codes: CptCode[]) => {
  if (!codes?.length) {
    return [{ code: { code: "" }, quantity: 1, id: uuidv4(), error: false }];
  }
  return [
    ...codes
      .reduce((acc: Map<string, CptInput>, cptCode: CptCode) => {
        const code = cptString(cptCode);
        if (acc.has(code)) {
          acc.get(code)!.quantity! += 1;
        } else {
          acc.set(code, {
            code: cptCode,
            quantity: 1,
            id: uuidv4(),
            error: false,
          });
        }
        return acc;
      }, new Map())
      .values(),
  ];
};

// Helper method for reconstructing CPT arrays for the payload for the API
export const createApiCptList = (codes: CptInput[]) =>
  codes.reduce((acc: CptCode[], cptCode: CptInput) => {
    return cptCode.code
      ? acc.concat(
          Array(cptCode.quantity || 0).fill({
            ...cptCode.code,
            modifier: cptCode.code.modifier,
          })
        )
      : acc;
  }, []);

// Helper method for building validation requirements
export const minimumRequirementsForDiagnosis = (d: CaseDiagnosis) => {
  return {
    diagnosis: !!d?.diagnosis?.trim(),
    microscopic: !!d?.microscopic?.trim(),
  };
};

// Helper method to check for any diagnostic information at all
// Used for preliminary reports
export const hasAnyDiagnosticInformation = (d: CaseDiagnosis) => {
  return (
    !!d?.diagnosis?.trim() || !!d?.microscopic?.trim() || !!d?.comment?.trim()
  );
};

// Helper method to format a specific diagnosis for validation
export const validityOfDiagnosis = (d: CaseDiagnosis) => {
  const requirements = minimumRequirementsForDiagnosis(d);
  return {
    specimen: d.specimen,
    missing: Object.entries(requirements)
      .filter(([_, valid]) => !valid)
      .map(([reason, _]) => reason),
    valid: Object.values(requirements).every(Boolean),
  };
};

// Helper method to determine if all slides have been viewed on a case per specimen
// screened cases will be limited to only the specimens required
export const relevantSlidesViewedOnCase = (
  caseDetailObject: CaseDetailState,
  currentSlideId?: number
) => {
  const mostRecentScreenRequest = getMostRecentScreenRequest(
    caseDetailObject?.screen_requests
  );
  return caseDetailObject.slides
    .filter((s) => {
      if (s.status !== Slide.status.SUCCESS) return false;
      if (
        caseDetailObject.is_screening &&
        s.specimen_id &&
        !mostRecentScreenRequest?.specimens?.includes(s.specimen_id)
      )
        return false;
      return true;
    })
    .every((s) => s.id === currentSlideId || s.was_previously_viewed);
};

// Helper method to determine if all relevant specimen have been diagnosed on a case
// Note that the context of `specimenDetail` is different depending on
// ... what user is looking at which case, and in what status the Case is in
// Abstracted out of `DiagnosisDialog` to be shared with SignOutButton.tsx
export const relevantSpecimenDiagnosedOnCase = (
  caseDetailObject: CaseDetailState,
  screenPerSpecimen?: boolean
) => {
  return caseDetailObject.specimenDetail
    .filter((spec) =>
      isSpecimenDiagnosable(caseDetailObject, spec, screenPerSpecimen)
    )
    .every((specimen) => specimen.diagnoses.length);
};

export const isSpecimenDiagnosable = (
  caseDetailObject: CaseDetailState,
  specimen: Specimen | SpecimenDetail,
  screenPerSpecimen?: boolean
) => {
  return caseDetailObject.is_screening
    ? isSpecimenScreening(caseDetailObject, specimen?.id)
    : screenPerSpecimen
    ? !isSpecimenScreened(caseDetailObject.case_diagnosis || [], specimen)
    : !isScreened(caseDetailObject.case_diagnosis);
};

// Function to check if the specimen is currently up for screening
export const isSpecimenScreening = (
  caseDetailObject: CaseDetailState,
  specimenId?: number
) => {
  const mostRecentScreenRequest = getMostRecentScreenRequest(
    caseDetailObject?.screen_requests
  );

  return (
    specimenId &&
    caseDetailObject.is_screening &&
    //Either the most recent screen request has the specimen in its list (new version - diagnosis per specimen)
    (mostRecentScreenRequest?.specimens?.includes(specimenId) ||
      //Or there are no screen requests (old version - diagnosis per case)
      !caseDetailObject.screen_requests?.length)
  );
};
